/**
 * Persistencia en IndexedDB del estado de subida RESUMABLE (F3-F). Sobrevive a
 * recargas: al reabrir la subida del mismo objetivo (`resumeKey`) se recupera
 * `{assetId, uploadId, uploadedParts}` y la máquina reanuda con list_parts.
 *
 * API mínima (get/put/del) sobre un único object store. Degrada a no-op si
 * IndexedDB no está disponible (SSR / navegadores restringidos) para no romper
 * la subida — solo se pierde la reanudación cross-reload.
 */

import type { CompletedPart, ResumeState } from "@space/shared";

const DB_NAME = "space-media-uploads";
const STORE = "resumable";
const DB_VERSION = 1;

/** Registro persistido de una subida en curso. */
export interface PersistedUpload {
  /** Clave estable del objetivo (p.ej. `${courseId}:${lessonId}:${size}`). */
  resumeKey: string;
  assetId: string;
  uploadId: string;
  uploadedParts: CompletedPart[];
  fileName: string;
  sizeBytes: number;
  title: string;
  updatedAt: number;
}

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    const idb = globalThis.indexedDB;
    if (!idb) {
      resolve(null);
      return;
    }
    let req: IDBOpenDBRequest;
    try {
      req = idb.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.addEventListener("upgradeneeded", () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "resumeKey" });
      }
    });
    req.addEventListener("success", () => resolve(req.result));
    req.addEventListener("error", () => resolve(null));
  });
}

function tx(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest,
): Promise<unknown> {
  return openDb().then(
    (db) =>
      new Promise<unknown>((resolve) => {
        if (!db) {
          resolve(null);
          return;
        }
        try {
          const store = db.transaction(STORE, mode).objectStore(STORE);
          const req = run(store);
          req.addEventListener("success", () => resolve(req.result ?? null));
          req.addEventListener("error", () => resolve(null));
        } catch {
          resolve(null);
        }
      }),
  );
}

/** Guarda/actualiza el estado de una subida resumable. */
export async function savePersistedUpload(record: PersistedUpload): Promise<void> {
  await tx("readwrite", (store) => store.put(record));
}

/** Recupera el estado persistido de un objetivo, o null si no hay. */
export async function loadPersistedUpload(resumeKey: string): Promise<PersistedUpload | null> {
  const result = await tx("readonly", (store) => store.get(resumeKey));
  // Trust-boundary read of the IndexedDB record; its shape is what we wrote.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return (result as PersistedUpload | null) ?? null;
}

/** Borra el estado persistido (subida completada o abortada). */
export async function clearPersistedUpload(resumeKey: string): Promise<void> {
  await tx("readwrite", (store) => store.delete(resumeKey));
}

/** Deriva el `ResumeState` de la máquina desde el registro persistido. */
export function toResumeState(record: PersistedUpload): ResumeState {
  return {
    assetId: record.assetId,
    uploadId: record.uploadId,
    uploadedParts: record.uploadedParts,
  };
}

/** Clave estable de reanudación para un archivo/objetivo dados. */
export function resumeKeyFor(params: {
  courseId?: string | null;
  lessonId?: string | null;
  classSessionId?: string | null;
  fileName: string;
  sizeBytes: number;
}): string {
  const scope = params.classSessionId ?? params.lessonId ?? params.courseId ?? "std";
  return `${scope}:${params.fileName}:${params.sizeBytes}`;
}
